import axios from "axios";
import { useContext, useEffect, useState, useCallback, useRef } from "react";
import { Container, Badge } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CountryContext } from "../context/CountryContext";
import StarRating from "../components/StarRating";
import BackToTop from "../components/BackToTop";
import WhatsappSupport from "../components/WhatsappSupport";
import "../styles/Home.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;
const PRODUCTS_PER_PAGE = 1; // Configurable page size

export default function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const [offerProducts, setOfferProducts] = useState([]);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState(null);
  const [displayLimit, ] = useState(7);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Banner state
  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const carouselRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const autoplayRef = useRef(null);
  const interactionTimeoutRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Hover state for product cards
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [productSizes, setProductSizes] = useState({});
  const [loadingSizes, setLoadingSizes] = useState({});
  const [enableSizes, setEnableSizes] = useState(true);

  const currencySymbols = { US: '$', UK: '£', SL: 'LKR' };
  const { country } = useContext(CountryContext);

  // Fetch company feature settings
  useEffect(() => {
    const fetchFeatureSettings = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/customer/company/get-feature-settings`, {
          params: { company_code: COMPANY_CODE }
        });
        setEnableSizes(response.data.enableSizes !== false);
      } catch (error) {
        console.error('Failed to fetch feature settings:', error);
        // Default to true if API fails
        setEnableSizes(true);
      }
    };
    fetchFeatureSettings();
  }, []);

  // Initialize page from URL params
  useEffect(() => {
    const page = parseInt(searchParams.get('page')) || 1;
    setCurrentPage(page);
  }, [searchParams]);

  const getProductDetails = (style_number) => {
    navigate(`/product/${style_number}`);
  };

  // Fetch available sizes for a product
  const fetchProductSizes = useCallback(async (styleId) => {
    if (productSizes[styleId]) {
      // Already loaded
      return;
    }

    setLoadingSizes(prev => ({ ...prev, [styleId]: true }));
    try {
      const url = `${BASE_URL}/api/customer/style-variants/${styleId}`;
      
      const response = await axios.get(url, {
        params: { company_code: COMPANY_CODE }
      });

      if (response.data && Array.isArray(response.data)) {
        // Extract unique sizes from variants
        const sizesSet = new Set();
        response.data.forEach(variant => {
          if (variant.size_name) {
            sizesSet.add(variant.size_name);
          }
        });
        
        const sizesList = Array.from(sizesSet).sort((a, b) => {
          // Sort sizes numerically if they are numbers, otherwise alphabetically
          const aNum = parseInt(a);
          const bNum = parseInt(b);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }
          return a.localeCompare(b);
        });

        setProductSizes(prev => ({
          ...prev,
          [styleId]: sizesList
        }));
      }
    } catch (error) {
      console.error('Failed to fetch product sizes:', error);
      // Set empty array on error
      setProductSizes(prev => ({
        ...prev,
        [styleId]: []
      }));
    } finally {
      setLoadingSizes(prev => ({ ...prev, [styleId]: false }));
    }
  }, [productSizes]);

  // Fetch products with pagination
  const fetchProducts = useCallback(async (page = 1, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const response = await axios.get(`${BASE_URL}/api/customer/all-styles`, {
        params: { 
          company_code: COMPANY_CODE,
          page: page,
          limit: PRODUCTS_PER_PAGE
        }
      });

      // Handle both new paginated response and old response format
      const responseData = response.data;
      let newProducts, total, hasMore;

      if (responseData.success && responseData.products) {
        // New paginated format
        newProducts = responseData.products;
        total = responseData.pagination.totalProducts;
        hasMore = responseData.pagination.hasMore;
      } else if (Array.isArray(responseData)) {
        newProducts = responseData;
        total = responseData.length;
        hasMore = false;
      } else {
        // Fallback
        newProducts = responseData || [];
        total = newProducts.length;
        hasMore = false;
      }
      
      if (append) {
        setProducts(prev => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
      }
      
      setTotalProducts(total);
      setHasMoreProducts(hasMore);

    } catch (error) {
      console.error('Error fetching product listings:', error);
      setError('Failed to load products');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProducts(currentPage, false);
  }, [currentPage, fetchProducts]);

  // Fetch banners for home page
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setBannersLoading(true);
        const response = await axios.get(`${BASE_URL}/api/customer/banners`, {
          params: {
            company_code: COMPANY_CODE,
            category: 'home'
          }
        });
        
        if (response.data.success) {
          setBanners(response.data.banners);
        } else {
          setBanners([]);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
        setBanners([]); // Fall back to empty array
      } finally {
        setBannersLoading(false);
      }
    };

    if (COMPANY_CODE) {
      fetchBanners();
    } else {
      console.warn('COMPANY_CODE is not defined');
      setBannersLoading(false);
    }
  }, []);

  // Load more products
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    
    // Update URL for SEO
    setSearchParams({ page: nextPage.toString() });
    
    fetchProducts(nextPage, true);
  };

  // fetch items with offer_price
  useEffect(() => {
    const fetchOfferProducts = async () => {
      try {
        setOfferLoading(true);
        setOfferError(null);

        const response = await axios.get(`${BASE_URL}/api/customer/offers`, {
          params: {company_code: COMPANY_CODE}
        });

        // Group products by style_id and select the best offer (minimum offer_price)
        const groupedProducts = {};
        response.data.forEach(product => {
          const styleId = product.style_id;
          
          if (!groupedProducts[styleId]) {
            groupedProducts[styleId] = product;
          } else {
            // If current product has lower offer_price, replace it
            if (product.offer_price < groupedProducts[styleId].offer_price) {
              groupedProducts[styleId] = product;
            }
          }
        });

        // Convert back to array
        const uniqueProducts = Object.values(groupedProducts);
        setOfferProducts(uniqueProducts);
      } catch (error) { 
        console.error('Error fetching offer products:', error);
        setOfferError('Failed to load offer products');
      } finally{
        setOfferLoading(false);
      }
    };
    fetchOfferProducts();
  }, []);


  // Filter to show only active offers (started AND not ended)
  const activeOfferProducts = offerProducts.filter((p) => {
    // Check if offer has valid dates and offer_price
    if (!p.offer_start_date || !p.offer_end_date || Number(p.offer_price) <= 0) {
      return false;
    }
    
    const startDate = new Date(p.offer_start_date);
    const endDate = new Date(p.offer_end_date);
    
    // Offer must have started AND not yet ended
    return startDate <= currentTime && endDate > currentTime;
  });

  const displayedProducts = activeOfferProducts.slice(0, displayLimit);

  // Fetch exchange rates
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/customer/currency/rates`);
        if (response.data.success) {
          setExchangeRates(response.data.rates);
        }
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error);
        setExchangeRates({
          GBP: 0.75,
          LKR: 320
        });
      }
    };
    fetchExchangeRates();
  }, []);

  const getRate = () => {
    switch (country) {
      case 'US':
        return 1;
      case 'UK':
        return exchangeRates['GBP'] || 0.75;
      case 'SL':
        return exchangeRates['LKR'] || 320;
      default:
        return 1;
    }
  };

  const formatPrice = (minPrice) => {
    if (!minPrice) return "Price not defined";
    const symbol = currencySymbols[country] || '$';
    const rate = getRate();
    const convertedPrice = (minPrice * rate).toFixed(2);
    return `${symbol}${convertedPrice}`;
  };

  // Utility function to calculate time left for a given end date
  const calculateTimeLeft = (endDate) => {
    const difference = new Date(endDate) - currentTime;

    if (difference <= 0) {
      return { expired: true };
    }

    return {
      expired: false,
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  // Timer effect for real-time countdown updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // controls for banner carousel
  useEffect(() => {
    if (!carouselRef.current) return;

    const startAutoplay = () => {
      if (banners.length <= 1) return;
      if (autoplayRef.current) clearInterval(autoplayRef.current);
      autoplayRef.current = setInterval(() => {
        setCurrentBannerIndex(prev => (prev + 1) % banners.length);
      }, 5000);
    };

    const stopAutoplay = () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };

    if (!isPaused) {
      startAutoplay();
    } else {
      stopAutoplay();
    }

    return () => stopAutoplay();
  }, [banners, isPaused]);

  // When index changes, scroll the target banner into view
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || banners.length === 0) return;
    const index = Math.min(Math.max(0, currentBannerIndex), banners.length - 1);
    const child = el.children[index];
    if (child) {
      // Use scrollLeft for horizontal carousel without affecting vertical scroll
      const scrollAmount = index * child.offsetWidth;
      el.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [currentBannerIndex, banners]);

  const goPrev = () => {
    if (!banners || banners.length === 0) return;
    setCurrentBannerIndex(i => (i - 1 + banners.length) % banners.length);
    setIsPaused(true);
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = setTimeout(() => setIsPaused(false), 8000);
  };

  const goNext = () => {
    if (!banners || banners.length === 0) return;
    setCurrentBannerIndex(i => (i + 1) % banners.length);
    setIsPaused(true);
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = setTimeout(() => setIsPaused(false), 8000);
  };


  return (
    <>
      {/* Banner Section */}
      <div className="home-banner mb-4">
        {bannersLoading ? (
          <div className="home-banner-loading">
            <div className="home-banner-skeleton"></div>
          </div>
        ) : banners.length > 0 ? (
          <div
            className="home-banner-carousel-wrapper"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            tabIndex={-1}
          >
            {banners.length > 1 && (
              <div className="home-banner-controls">
                <button
                  className="home-banner-prev"
                  onClick={goPrev}
                  aria-label="Previous banner"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  className="home-banner-next"
                  onClick={goNext}
                  aria-label="Next banner"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            )}

            <div
              className="home-banner-carousel"
              ref={carouselRef}
            >
              {banners.map((banner, idx) => {
                const filename = banner.banner_url || banner.image_url || banner.imageUrl;
                const key = banner.banner_id || banner.id || idx;
                return (
                  <img
                    key={key}
                    src={`${BASE_URL}/uploads/banners/${filename}`}
                    className="home-banner-img"
                    alt={banner.alt_text || `Home banner ${idx + 1}`}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <div className="home-banner-placeholder">
            <h2>Welcome to Our Store</h2>
            <p>Discover our latest collections and offers!</p>
          </div>
        )}
      </div>

      {/* Offer Products Section */}
      {!offerLoading && !offerError && activeOfferProducts.length > 0 && (
        <Container fluid className="my-5 homepage-offer-products-container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="homepage-offer-title-section">
              <h2 className="homepage-offer-title">
                <i className="fas fa-fire text-danger me-2 "></i>
                Hot Deals & Offers
              </h2>
              <p className="hp-offer-des">
                * Offer prices are valid only for selected size/color combinations.{" "}
              </p>
          </div>
            <button
              className="btn btn-outline-primary btn-sm view-all-offers-btn"
              onClick={() => {
                navigate('/offers');
              }}
            >
              View All Offers
            </button>
          </div>

          <div className="homepage-offer-products-grid">
            {displayedProducts.map((product) => {
              const timeLeft = calculateTimeLeft(product.offer_end_date);

              return (
                <div
                  key={product.style_id}
                  className="homepage-offer-product-card"
                  onClick={() => getProductDetails(product.style_number)}
                >
                  <div className="homepage-offer-product-image-container">
                    <div className="discount-badge">
                      {product.discount_percentage > 0 && (
                        <span className="discount-percentage">
                          -{product.discount_percentage}%
                        </span>
                      )}
                    </div>

                    <img
                      src={`${BASE_URL}/uploads/styles/${product.image}`}
                      alt={product.name}
                      className="offer-product-image"
                    />
                  </div>

                  <div className="offer-product-info">
                    <h4 className="offer-product-name">{product.name}</h4>
                    <p className="home-product-description">
                      {product.description && product.description.length > 100
                        ? `${product.description.substring(0, 100)}...`
                        : product.description}
                    </p>

                    {/* Rating Display */}
                  {parseFloat(product.average_rating) > 0 && parseInt(product.review_count) > 0 && (
                    <div className="product-rating mb-2">
                       <StarRating rating={Math.round(parseFloat(product.average_rating))} size="medium" />
                       <span className="rating-text ms-2">
                        {parseFloat(product.average_rating).toFixed(1)}({parseInt(product.review_count)})
                      </span>
                    </div>
                  )}

                    <div className="offer-product-price">
                      <span className="current-price">
                        {formatPrice(product.offer_price)}
                      </span>
                      <span className="original-price">
                        {formatPrice(product.sale_price)}
                      </span>
                    </div>

                    <div className="countdown-timer">
                      {timeLeft.expired ? (
                        <div className="countdown-label text-danger" style={{fontSize: '0.9rem' }}>Offer Ended</div>
                      ) : (
                        <>
                          <div className="countdown-display text-danger" style={{fontSize: '0.9rem' }}>
                            {timeLeft.days > 0 && (
                              <span className="time-unit">
                                <span className="time-value">{timeLeft.days}</span>
                                <span className="time-label">d </span>
                              </span>
                            )}
                            <span className="time-unit">
                              <span className="time-value">
                                {timeLeft.hours.toString().padStart(2, "0")}
                              </span>
                              <span className="time-label">h </span>
                            </span>
                            <span className="time-unit">
                              <span className="time-value">
                                {timeLeft.minutes.toString().padStart(2, "0")}
                              </span>
                              <span className="time-label">m </span>
                            </span>
                            <span className="time-unit">
                              <span className="time-value">
                                {timeLeft.seconds.toString().padStart(2, "0")}
                              </span>
                              <span className="time-label">s </span>
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {product.category_name && (
                      <div className="offer-product-category">
                        <span className="offer-category-badge">
                          {product.category_name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      )}

      {/* Main Products Section */}
      <Container fluid className="my-5 home-products-container">
        <h2 className="mb-4">Explore Your Fashion</h2>

        {loading ? (
          <div className="text-center my-5">
            <div className="home-loading-spinner">
              <div className="home-spinner"></div>
              <p>Loading products...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center my-5">
            <h5 className="text-danger">{error}</h5>
          </div>
        ) : products.length === 0 ? (
          <div className="home-no-products">
            <div className="home-no-products-content">
              <i className="fas fa-search fa-3x"></i>
              <h3>No products found</h3>
              <p>No products available at the moment.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="home-products-grid">
              {products.map((product) => (
                <div
                  key={product.style_id}
                  className="home-product-card"
                  onClick={() => getProductDetails(product.style_number)}
                  onMouseEnter={() => {
                    setHoveredProductId(product.style_id);
                    fetchProductSizes(product.style_id);
                  }}
                  onMouseLeave={() => setHoveredProductId(null)}
                >
                  <div className="home-product-image-container">
                    <img
                      src={`${BASE_URL}/uploads/styles/${product.image}`}
                      alt={product.name}
                      className="home-product-image"
                    />

                    <div className="home-product-overlay">
                      <h5>Quick View</h5>
                    </div>

                    {/* Size Overlay - appears on hover */}
                    {enableSizes && (
                      <div 
                        className={`home-product-sizes-overlay ${
                          hoveredProductId === product.style_id ? 'show' : ''
                        }`}
                      >
                        <div className="sizes-content">
                          {/* <p className="sizes-label">Available Sizes:</p> */}
                          {loadingSizes[product.style_id] ? (
                            <div className="text-center py-2">
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            </div>
                          ) : (productSizes[product.style_id] && productSizes[product.style_id].length > 0) ? (
                            <div className="sizes-badges">
                              {productSizes[product.style_id].map((size, idx) => (
                                <Badge 
                                  key={idx}
                                  bg="light" 
                                  text="dark"
                                >
                                  {size}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-muted small">No sizes available</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="home-product-info">
                    <h3 className="home-product-name">{product.name}</h3>
                    <p className="home-product-description">
                      {product.description && product.description.length > 100
                        ? `${product.description.substring(0, 100)}...`
                        : product.description
                      }
                    </p>
                    
                    {/* Rating Display */}
                    {parseFloat(product.average_rating) > 0 && parseInt(product.review_count) > 0 && (
                      <div className="product-rating mb-2">
                         <StarRating rating={Math.round(parseFloat(product.average_rating))} size="medium" />
                        <span className="rating-text ms-2">
                          {parseFloat(product.average_rating).toFixed(1)}({parseInt(product.review_count)})
                        </span>
                      </div>
                    )}
                    
                    <div className="home-product-price">
                      {product.offer_price && product.offer_price !== 0 && 
                      activeOfferProducts.some(p => p.style_id === product.style_id) ? (
                        <>
                          <span className="me-2">
                            {formatPrice(product.offer_price)}
                          </span>
                          <span className="text-muted text-decoration-line-through small">
                            {formatPrice(product.sale_price)}
                          </span>
                        </>
                      ) : (
                        <span>{formatPrice(product.sale_price)}</span>
                      )}
                    </div>
                    {/* {product.category_name && (
                      <div className="home-product-category">
                        <span className="home-category-badge">
                          {product.category_name}
                        </span>
                      </div>
                    )} */}
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Section */}
            {hasMoreProducts && (
              <div className="text-center my-5">
                <button
                  className="btn btn-primary load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Loading More...
                    </>
                  ) : (
                    <>
                      Load More Products
                      <i className="fas fa-chevron-down ms-2"></i>
                    </>
                  )}
                </button>
                <div className="mt-2 text-muted small">
                  Showing {products.length} of {totalProducts} products
                </div>
              </div>
            )}

            {/* End of results message */}
            {!hasMoreProducts && products.length > 0 && (
              <div className="text-center my-5">
                <div className="end-of-results">
                  <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                  <p className="text-muted">You've seen all our amazing products!</p>
                </div>
              </div>
            )}
          </>
        )}
      </Container>

      <BackToTop />
      <WhatsappSupport message="Hi, I need help with your products!" />
    </>
  );
}